import { useCallback, useContext } from 'react';
import { Capacitor } from '@capacitor/core';
import { CREATE_PLAYER, LOGIN_PLAYER, ONLINE } from 'constants/eventConstants';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import { useAppSelector } from 'hooks';
import Cookies from 'js-cookie';
import {
  extractAccessTokenFromDescopeCookie,
  extractIDTokenFromDescopeCookie,
  extractRefreshTokenFromDescopeCookie
} from 'utils/functions';
import { APP_DOMAIN, DESCOPE_COOKIE } from 'utils/urls';

const useAuthService = () => {
  const { socket } = useContext(SocketContext);
  const displayName = useAppSelector((state) => state.user.displayName);
  const getToken = useCallback(() => {
    // Try cookie first, then localStorage fallback (iOS WKWebView drops cookies)
    let descopeCookie = Cookies.get(DESCOPE_COOKIE as string);
    if (!descopeCookie || typeof descopeCookie !== 'string') {
      try {
        const stored = localStorage.getItem(DESCOPE_COOKIE as string) || '';
        if (stored.startsWith('{') || stored.startsWith('%7B')) {
          descopeCookie = stored;
        }
      } catch { /* noop */ }
    }

    if (!descopeCookie || typeof descopeCookie !== 'string') {
      if (!Capacitor.isNativePlatform()) {
        window.location.replace(`https://auth.${APP_DOMAIN}/loginapp`);
      }
      return {
        idToken: null,
        descopeSession: null,
        descopeRefresh: null
      };
    }

    return {
      idToken: extractIDTokenFromDescopeCookie(descopeCookie),
      descopeSession: extractAccessTokenFromDescopeCookie(descopeCookie),
      descopeRefresh: extractRefreshTokenFromDescopeCookie(descopeCookie)
    };
  }, []);

  const createPlayer = useCallback(() => {
    const { descopeSession } = getToken();

    if (!descopeSession) {
      // On native, App.tsx shows LoginScreen — don't redirect here
      if (!Capacitor.isNativePlatform()) {
        window.location.replace(`https://auth.${APP_DOMAIN}/loginapp`);
      }
      return;
    }

    //Attempt to create account assuming first visit
    socket.emit(CREATE_PLAYER, {
      jwt: descopeSession,
      key: 'AUEEW891WL',
      language: 'en'
    });
  }, [getToken, socket]);

  const loginPlayer = useCallback(
    (data: any) => {
      const { descopeSession } = getToken();

      // Attempt to register user from token
      if (
        typeof data?.response?.error === 'string' &&
        data.response.error.startsWith('Email already in use')
      ) {
        // User already exists, Attempt to login
        if (!displayName) {
          socket.emit(LOGIN_PLAYER, {
            jwt: descopeSession,
            deviceID:
              localStorage.getItem('deviceID') === null ? '' : localStorage.getItem('deviceID'),
            language: 'en',
            key: 'AUEEW891WL' // Key should not be passed here, store as eNV on engine side instead
          });
        }
      } else if (data?.response?.playerName !== 'NOUSER') {
        socket.emit(LOGIN_PLAYER, {
          jwt: descopeSession,
          deviceID:
            localStorage.getItem('deviceID') === null ? '' : localStorage.getItem('deviceID'),
          language: 'en',
          key: 'AUEEW891WL' //Key should not be passed here, store as eNV on engine side instead
        });
      }
    },
    [displayName, getToken, socket]
  );

  const online = useCallback(
    (playerGUID: string) => {
      socket.emit(ONLINE, {
        playerGUID
      });
    },
    [socket]
  );

  return {
    createPlayer,
    loginPlayer,
    online
  };
};

export default useAuthService;
